import { formatPhone } from "@majitap/ui";
import { Badge } from "@majitap/ui/arc/badge/badge";
import { Link } from "@tanstack/react-router";
import { Phone, X } from "lucide-react";
import type { ReactNode } from "react";
import { initialsOf } from "../../dispatch";
import type { Tone } from "../../labels";
import styles from "./board.module.css";

/**
 * The side pane's parts (`Console / Dispatch / …`, right): a head with the delivery and its
 * status, Open and close; titled sections; a person to call.
 */
export function PaneHead({
  title,
  status,
  sub,
  open,
  onClose,
  aside,
}: {
  title: string;
  status?: [label: string, tone: Tone];
  sub?: ReactNode;
  /** the delivery's own page */
  open?: string;
  onClose?: () => void;
  aside?: ReactNode;
}) {
  return (
    <header className={styles.sideHead}>
      <div className={styles.sideTop}>
        <h2 className={styles.sideTitle}>{title}</h2>
        {status ? <Badge tone={status[1]}>{status[0]}</Badge> : null}
        <span className={styles.sideTools}>
          {aside}
          {open ? (
            <Link to={open} className={styles.link}>
              Open
            </Link>
          ) : null}
          {onClose ? (
            <button type="button" className={styles.iconButton} aria-label="Close" onClick={onClose}>
              <X size={16} aria-hidden />
            </button>
          ) : null}
        </span>
      </div>
      {sub ? <p className={styles.small}>{sub}</p> : null}
    </header>
  );
}

export function PaneSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className={styles.paneSection} aria-label={title}>
      <h3 className={styles.sectionLabel}>{title}</h3>
      {children}
    </section>
  );
}

/** A label and its value, with a line under it: "Fill site · Kabete · 6.1 km from the customer". */
export function Fact({ label, value, note }: { label: string; value: ReactNode; note?: ReactNode }) {
  return (
    <div className={styles.fact}>
      <dt>{label}</dt>
      <dd>
        <span>{value}</span>
        {note ? <span className={styles.small}>{note}</span> : null}
      </dd>
    </div>
  );
}

export function Avatar({ name }: { name: string }) {
  return (
    <span className={styles.avatar} aria-hidden>
      {initialsOf(name)}
    </span>
  );
}

/** Someone to call: their initials, name and role, and a call button when there's a number. */
export function Person({ name, detail, phone }: { name: string; detail: ReactNode; phone?: string | null }) {
  return (
    <div className={styles.person}>
      <Avatar name={name} />
      <span className={styles.optionBody}>
        <strong>{name}</strong>
        <span className={styles.small}>{detail}</span>
      </span>
      {phone ? (
        <a className={styles.callButton} href={`tel:${phone}`} aria-label={`Call ${name}, ${formatPhone(phone)}`}>
          <Phone size={16} aria-hidden />
        </a>
      ) : null}
    </div>
  );
}
